import React, { useState, useEffect, useRef } from 'react';
import {
    Anchor,
    Box,
    Heading,
    Page,
    PageContent,
    DataTable,
    Pagination,
} from 'grommet';
import {
    API,
    resolveSortField,
    RowsPerPageSelect,
    usePaginatedSort,
} from '@enrich/shared-lib';
import QueryString from 'qs';
import { AddPipelineButton } from './AddPipelineButton';
import { useLoaderData } from 'react-router-dom';

const STEP_OPTIONS = [10, 25, 50, 100];
const DEFAULT_SORT = { property: 'attributes.name', direction: 'asc' };

export const PipelineDashboard = () => {
    const { loadedPipelines, loadedCompanies } = useLoaderData();
    const companies = (loadedCompanies?.data || [])
        .map((company) => ({ ...company.attributes, id: company.id }))
        .sort((a, b) => b.name < a.name);
    const paginationMeta = loadedPipelines?.meta?.pagination;
    const [pipelines, setPipelines] = useState(loadedPipelines?.data || []);
    const {
        page,
        pageSize,
        sort,
        totalItems,
        stepOptions,
        handleSort,
        handlePageChange,
        handlePageSizeChange,
        applyPagination,
    } = usePaginatedSort({
        initialSort: DEFAULT_SORT,
        stepOptions: STEP_OPTIONS,
        initialPage: paginationMeta?.page ?? 1,
        initialPageSize: paginationMeta?.pageSize ?? STEP_OPTIONS[0],
        initialTotalItems: paginationMeta?.total ?? 0,
    });

    // The loader already fetched the first page with DEFAULT_SORT, so the
    // mount run of this effect would duplicate that request.
    const isFirstRun = useRef(true);

    useEffect(() => {
        if (isFirstRun.current) {
            isFirstRun.current = false;
            return;
        }
        if (!page) return;
        let isActive = true;

        (async () => {
            const response = await loadPipelines({ page, pageSize, sort });
            if (isActive && response?.meta?.pagination) {
                setPipelines(response.data);
                applyPagination(response.meta);
            }
        })();

        return () => {
            isActive = false;
        };
    }, [page, pageSize, sort, applyPagination]);

    const dataColumns = [
        {
            header: 'Name',
            property: 'attributes.name',
            render: (pipeline) => {
                return (
                    <Anchor
                        href={`/pipelines/${pipeline.id}`}
                        label={pipeline.attributes.name}
                    />
                );
            },
            search: true,
            sortable: true,
        },
        {
            header: 'Company',
            property: 'attributes.company.data.attributes.name',
            sortable: true,
        },
    ];

    return (
        <Page
            kind="full"
            pad={{ top: '30px', horizontal: 'medium', bottom: '30px' }}>
            <PageContent>
                <Box direction="row-responsive" gap="medium">
                    <Box>
                        <Heading level="2">Pipelines</Heading>
                    </Box>
                    <Box alignSelf="center">
                        <AddPipelineButton companies={companies} />
                    </Box>
                </Box>
                <Box elevation="medium" pad="medium">
                    <Box>
                        <DataTable
                            primaryKey="id"
                            sort={{ ...sort, external: true }}
                            onSort={handleSort}
                            data={pipelines}
                            columns={dataColumns}
                        />
                    </Box>
                    <Box
                        direction="row-responsive"
                        align="center"
                        justify="between"
                        gap="small"
                        pad="small">
                        <RowsPerPageSelect
                            options={stepOptions}
                            value={pageSize}
                            onChange={handlePageSizeChange}
                        />
                        <Pagination
                            numberItems={totalItems}
                            page={page}
                            onChange={handlePageChange}
                            step={pageSize}
                            summary={true}
                        />
                    </Box>
                </Box>
            </PageContent>
        </Page>
    );
};

const SORT_FIELD_MAP = {
    'attributes.name': 'name',
    'attributes.company.data.attributes.name': 'company.name',
};

async function loadPipelines({
    page = 1,
    pageSize = STEP_OPTIONS[0],
    sort = DEFAULT_SORT,
} = {}) {
    const sortField = resolveSortField(sort, SORT_FIELD_MAP, 'createdAt');
    const sortDir = sort?.direction ?? 'desc';
    const query = QueryString.stringify(
        {
            fields: ['id', 'name'],
            populate: { company: { fields: ['id', 'name'] } },
            pagination: page && pageSize ? { page, pageSize } : undefined,
            sort: [`${sortField}:${sortDir}`],
        },
        { encodeValuesOnly: true },
    );

    try {
        const response = await API.get(`/api/pipelines?${query}`);
        return response.data;
    } catch (error) {
        console.log(error);
    }

    return null;
}

async function loadCompanies() {
    const query = QueryString.stringify(
        {
            fields: ['id', 'name'],
            sort: ['name:asc'],
            pagination: { limit: 10000 },
        },
        { encodeValuesOnly: true },
    );

    try {
        const response = await API.get(`/api/companies?${query}`);
        return response.data;
    } catch (error) {
        console.log(error);
    }

    return null;
}

export async function pipelineDashboardLoader() {
    const result = await Promise.allSettled([loadPipelines(), loadCompanies()]);
    const settled = (entry) =>
        entry.status === 'fulfilled' ? entry.value : null;

    return {
        loadedPipelines: settled(result[0]),
        loadedCompanies: settled(result[1]),
    };
}
